<template>
  <div id="document-info" class="document-info">
    <!-- REQUEST DETAILS -->
    <div data-test-id="requestor-details">
      <!-- TITLE -->
      <div class="sign-requirement-title pb-1">
        {{ i18n.t('titles.requestor') }}
      </div>
      <!-- /TITLE -->

      <!-- REQUESTOR ORGANIZATION AND NAME -->
      <div
        class="d-flex align-center flex-wrap text"
        data-test-id="requestor-org-name"
      >
        <div
          class="d-flex align-center"
          :data-test-id="`organization-${initiatorCompanyDisplayName}`"
        >
          {{ initiatorCompanyDisplayName }}
          <v-tooltip
            v-model="showVerificationTooltip"
            :aria-label="verificationText"
            :close-delay="1000"
            content-class="tooltip deep-light tooltip-top bg-white"
            location="top"
            open-on-click
          >
            <template #activator="{ props: ActivatorProps }">
              <v-icon
                v-bind="ActivatorProps"
                aria-label="Verification icon"
                class="mx-1"
                :color="verificationIconColor"
                :size="14"
              >
                {{ verificationIcon }}
              </v-icon>
            </template>
            <div class="verification-badge-details">
              {{ verificationText }}
              <br />
              <a
                class="tooltip-link text-primary pointer-events-auto"
                :href="String(i18n.t('texts.verification_badge_details.link'))"
                rel="noopener"
                target="_blank"
                >{{ i18n.t('texts.learn_more') }}</a
              >
            </div>
          </v-tooltip>
        </div>
        <span class="flex-shrink-0" data-test-id="requestor-display-name">
          ({{ requestorDisplayName }})
        </span>
      </div>
      <!-- /REQUESTOR ORGANIZATION AND NAME -->

      <!-- REQUESTOR EMAIL -->
      <div
        class="sub-text mb-2"
        :data-test-id="`requestor-email-${requestorEmail}`"
      >
        {{ requestorEmail }}
      </div>
      <!-- /REQUESTOR EMAIL -->

      <!-- DATE TIME -->
      <span
        v-if="documentStartTime"
        v-tooltip="{
          text: timestampToDate(documentStartTime),
          location: 'right',
          offset: 4,
          openDelay: 300
        }"
        :aria-label="timestampToDate(documentStartTime)"
        class="document-time d-flex flex-row align-center sub-text"
        data-test-id="date-time"
      >
        {{ formatDateToLocale(documentStartTime, i18n.locale.value, true) }}
      </span>
      <!-- DATE TIME -->

      <div
        v-if="
          document?.jurisdiction &&
          document?.signatureMode &&
          !isSignatureTypeSeal
        "
      >
        <v-divider class="my-5" />
        <DocumentSignatureLevel
          :jurisdiction="document?.jurisdiction"
          :signature-mode="document?.signatureMode"
        />
        <!-- <v-divider class="my-5" /> -->
      </div>
    </div>
    <!-- /REQUEST DETAILS -->

    <!-- COMMENT FROM REQUESTOR -->
    <template v-if="documentComment">
      <v-divider class="my-5" />
      <div>
        <div class="sign-requirement-title">
          {{ i18n.t('titles.message') }}
        </div>
        <div
          v-if="
            document &&
            document.initiatorCompanyVerificationType ===
              VerificationState.STRONG
          "
          id="message-preview"
          class="text-body-large font-weight-medium"
          data-test-id="message-preview"
          v-html="fixedHrefsComment"
        ></div>
        <div v-else>
          <pre class="text-body-large font-weight-medium text-pre-wrap">{{
            comment
          }}</pre>
        </div>
      </div>
    </template>
    <!-- /COMMENT FROM REQUESTOR -->

    <!-- SIGNEE/APPROVER ADD COMMENT -->
    <div
      v-if="showMessageToRequestorButton && !toggleMessageField"
      class="add-message-button-container"
    >
      <v-divider class="my-5" />

      <button class="add-message-button" @click="showMessageField">
        {{ i18n.t('buttons.add_message') }}
      </button>
    </div>
    <div v-if="toggleMessageField && showMessageToRequestorButton">
      <v-textarea
        ref="addMessageTextArea"
        v-model="message"
        class="mt-4"
        clearable
        hide-details
        :label="i18n.t('labels.add_message')"
        no-resize
        variant="outlined"
        @blur="updateDocumentMessage"
        @focus="setInitialMessage"
      />
    </div>
    <!-- /SIGNEE ADD COMMENT -->

    <v-divider v-if="!isSignatureTypeSeal" class="mt-5 mb-2" />
    <v-expansion-panels
      v-if="!isSignatureTypeSeal"
      v-model="panel"
      data-test-id="invited-signatories-panel"
      flat
      multiple
      variant="accordion"
    >
      <!-- INVITED SIGNEES -->
      <InvitedSignatories />

      <!-- INVITED SIGNEES -->

      <!-- OBSERVERS -->
      <v-expansion-panel v-show="hasObservers" :value="false" @click="scroll">
        <v-expansion-panel-title
          class="drawer-subhead expansion-panel-title"
          data-test-id="observers-expansion-panel"
          static
        >
          {{ i18n.t('titles.observers') }}
          <template #actions="{ expanded }">
            <VIcon :class="{ 'fa-rotate-180': expanded }" :size="12">
              far fa-chevron-down
            </VIcon>
          </template>
        </v-expansion-panel-title>
        <v-expansion-panel-text
          v-if="document && document.observers"
          class="observer-panel-content"
        >
          <div class="d-flex flex-column ga-4">
            <template v-for="obs in document.observers" :key="obs.observerId">
              <ObserverItem :observer="obs" />
            </template>
          </div>
        </v-expansion-panel-text>
      </v-expansion-panel>
      <!-- /OBSERVERS -->

      <!-- Attachments are shown in the left PdfSidebar. -->
      <!-- DeepA Summarize -->
      <DeepASummarizeDocumentPanel :document />
      <!-- /DeepA Summarize -->
    </v-expansion-panels>

    <!-- /ATTACHMENTS -->

    <!-- ACTIONS -->
    <template v-if="showActions">
      <v-divider class="my-4 mb-5" />
      <div class="d-flex flex-row justify-center">
        <div class="button-container">
          <DeepButton
            v-if="
              SignatureService.isSignedByUser(me, signees) &&
              route.name !== 'sign-id' &&
              !isDocumentAuthRoute
            "
            :action="goBackToOverview"
            class="mt-3"
            :text="i18n.t('buttons.overview')"
          />
          <DeepButton
            v-if="document && document.documentUrl"
            :action="download"
            class="mt-2"
            data-test-id="download-document"
            :text="
              hasMultipleFiles
                ? i18n.t('buttons.download_all')
                : i18n.t('buttons.download')
            "
            variant="outlined"
          />
        </div>
      </div>
    </template>
    <!-- /ACTIONS -->
  </div>
</template>

<script setup lang="ts">
import ObserverItem from '@/components/items/ObserverItem.vue';
import InvitedSignatories from '@/components/InvitedSignatories.vue';
import DocumentSignatureLevel from '@/components/signatureArea/DocumentSignatureLevel.vue';
import DeepButton from '@/components/buttons/DeepButton.vue';
import DOMPurify from 'dompurify';
import {
  downloadDocumentPdf,
  downloadEnvelopeFilesAsZip
} from '@/services/documentService';
import { addHrefSchemes } from '@/services/utilsService';
import AuthService from '@/services/authService.ts';
import { VerificationState } from '@/types/enums/VerificationState';
import SignatureService from '@/services/signatureService';
import { computed, nextTick, type PropType, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { formatDateToLocale } from '@/composables/useUtils';
import type { Document } from '@/types/Document';
import { useDocumentContext } from '@/composables/useDocumentContext';
import { useSignContext } from '@/composables/useSignContext';
import { timestampToDate } from '@deepcloud/deep-ui-utils/date';
import DeepASummarizeDocumentPanel from '@/components/deepa/DeepASummarizeDocumentPanel.vue';

const props = defineProps({
  document: {
    type: Object as PropType<Document>,
    default: undefined
  },
  signees: {
    type: Array,
    default: () => []
  }
});

const emit = defineEmits(['update-signature-message']);

const { isDocumentAuthRoute } = useDocumentContext();

const panel = ref([0, 1]);
const toggleMessageField = ref(false);
const message = ref('');
const initialMessage = ref('');
const showVerificationTooltip = ref(false);
const i18n = useI18n();
const route = useRoute();
const router = useRouter();
const { isSignatureTypeSeal } = useSignContext();
const addMessageTextArea = ref();
const comment = computed(() => {
  return props.document?.comment
    ? props.document?.comment
        .replaceAll('<br/>', '\n')
        .replaceAll('<br />', '\n')
    : '';
});

const requestorEmail = computed(() => {
  if (props.document) {
    return props.document.initiatorDisplayEmail;
  }
  return undefined;
});

const requestorDisplayName = computed(() => {
  if (props.document) {
    return props.document.initiatorDisplayName;
  }
  return undefined;
});

const isRequestor = computed(() => {
  if (props.document) {
    return SignatureService.compareString(
      props.document.initiatorDisplayEmail,
      AuthService.user?.email
    );
  }
  return false;
});

const initiatorCompanyDisplayName = computed(() => {
  return props.document?.initiatorCompanyDisplayName || '';
});

const verificationIcon = computed(() => {
  return props.document?.initiatorCompanyVerificationType ===
    VerificationState.STRONG
    ? 'fas fa-shield-check'
    : 'fas fa-shield-xmark';
});

const verificationIconColor = computed(() => {
  return props.document?.initiatorCompanyVerificationType ===
    VerificationState.STRONG
    ? '#0052ff'
    : 'warning';
});

const verificationText = computed(() => {
  return props.document?.initiatorCompanyVerificationType ===
    VerificationState.STRONG
    ? i18n.t('texts.verification_badge_details.verified')
    : i18n.t('texts.verification_badge_details.unverified');
});

const me = computed(() => {
  return props.document?.signContext?.email;
});

const showMessageToRequestorButton = computed(() => {
  if (me.value && props.document) {
    return (
      (!isRequestor.value &&
        SignatureService.canApprove(me.value, props.document)) ||
      SignatureService.canSign(me.value, props.document)
    );
  } else {
    return false;
  }
});

const showActions = computed(() => {
  return (
    props.document?.documentUrl ||
    (me.value &&
      SignatureService.isSignedByUser(me.value, props.signees) &&
      route.name !== 'sign-id')
  );
});

const sanitizedComment = computed<string | null>(() => {
  const raw = documentComment.value?.trim();
  if (!raw || !props.document?.comment) return null;

  const dirty = DOMPurify.sanitize(raw, {
    ALLOWED_TAGS: [
      'a',
      'abbr',
      'acronym',
      'b',
      'blockquote',
      'br',
      'code',
      'em',
      'i',
      'li',
      'ol',
      'strong',
      'ul'
    ],
    ALLOWED_ATTR: ['href', 'src', 'target', 'rel'],
    ALLOWED_URI_REGEXP:
      /^(?:(?:(?:f|ht)tps?|mailto|tel):|[^a-z]|[a-z+.]+(?:[^a-z+.\-:]|$))/i
  });

  const withBlank = dirty.replace(
    /<a\s+(?![^>]*\btarget=)(?![^>]*\brel=)([^>]*?)>/g,
    '<a target="_blank" rel="noopener noreferrer" $1>'
  );

  return withBlank
    .replace(/(<ul>|<ol>)\s*<br\s*\/?>/g, '$1')
    .replace(/(<\/li>)\s*<br\s*\/?>/g, '$1')
    .replace(/(<\/ul>|<\/ol>)\s*(?:<br\s*\/?>\s*)+(?=<\/?(?:ul|ol)>)/g, '$1');
});

const fixedHrefsComment = computed(() => {
  if (sanitizedComment.value) {
    return addHrefSchemes(sanitizedComment.value);
  } else {
    return null;
  }
});

const hasObservers = computed(() => {
  return props.document?.observers?.length > 0 || false;
});

const hasMultipleFiles = computed(() => {
  return (props.document?.files?.length ?? 0) > 1;
});

const documentStartTime = computed(() => {
  if (props.document) {
    return props.document.startTime;
  }
  return undefined;
});

const documentComment = computed(() => {
  if (props.document) {
    return props.document.comment;
  }
  return undefined;
});

function scroll() {
  setTimeout(() => {
    const documentInfo = document.getElementById('document-info');
    if (documentInfo) {
      documentInfo.scrollIntoView({
        block: 'nearest',
        inline: 'nearest',
        behavior: 'smooth'
      });
    }
  }, 200);
}

function setInitialMessage() {
  initialMessage.value = message.value;
}

function updateDocumentMessage() {
  if (message.value !== initialMessage.value) {
    emit('update-signature-message', message.value);
  }
}

function showMessageField() {
  toggleMessageField.value = !toggleMessageField.value;
  nextTick(() => {
    addMessageTextArea.value.focus();
  });
}

async function download() {
  try {
    if (hasMultipleFiles.value && props.document?.files?.length) {
      await downloadEnvelopeFilesAsZip(
        props.document.documentId,
        props.document.documentName,
        props.document.files
      );
      return;
    }

    await downloadDocumentPdf(
      props.document?.documentName,
      props.document?.documentStatus,
      props.document?.documentId
    );
  } catch (error) {
    console.error('Error downloading document ', error);
  }
}

function goBackToOverview() {
  const tabStatus = sessionStorage.getItem('filterStatus');
  if (tabStatus !== 'undefined') {
    return router.push({ name: 'dashboard', query: { status: tabStatus } });
  }
  return router.push({ name: 'dashboard' });
}
</script>

<style lang="scss" scoped>
:deep(.v-expansion-panel-title__overlay) {
  background-color: white !important;
  height: 12px;
}
.expansion-panel-title {
  padding: 0 !important;
  min-height: 49px !important;
}

.expansion-panel-title .v-expansion-panel-title__overlay .container {
  margin-top: 20px;
  padding: 16px;
  max-width: 100%;
  border: 1px solid rgb(var(--v-theme-grey-lighten-2));
  border-radius: 7px;
}

.document-info {
  margin-top: 12px;
  padding: 16px;
  max-width: 100%;
  border: 1px solid rgb(var(--v-theme-grey-lighten-2));
  border-radius: 7px;
}

.document-time {
  width: fit-content;
}

.text {
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 0.005em;
  font-feature-settings: 'liga' off;
  color: black;
}

.sub-text {
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 20px;
  color: rgb(var(--v-theme-grey-darken-1));
  margin-bottom: 12px;
}

.add-message-button {
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 36px;
  letter-spacing: 0.0125em;
  color: rgb(var(--v-theme-primary));
}

.button-container {
  width: 380px;
  padding: 0;
}

.observer-panel-content :deep(.v-expansion-panel-text__wrapper) {
  padding-top: 4px;
  padding-bottom: 4px;
}

.signees-panel-content :deep(.v-expansion-panel-content__wrap) {
  padding-top: 0;
  padding-left: 2px;
  padding-right: 0 !important;
}

.signees-panel-content :deep(.v-expansion-panel-text__wrapper) {
  padding-top: 0;
}

.signees-ordered-panel-content :deep(.v-expansion-panel-content__wrap) {
  padding-left: 0;
  margin-top: 16px;
  padding-right: 0 !important;
}

.signees-ordered-panel-content :deep(.v-expansion-panel-text__wrapper) {
  padding-top: 4px;
  padding-bottom: 4px;
}

.tooltip {
  box-shadow: 0 3px 10px rgb(0 0 0 / 0.2) !important;
}

.verification-badge-details {
  max-width: 184px;
  font-size: 13px;
}

.tooltip-link {
  text-decoration: none;
}

.group-container {
  padding-right: 12px;
  background-color: white;
  margin-bottom: 12px;
}

.v-textarea :deep(.v-field__clearable .v-icon) {
  color: rgb(var(--v-theme-primary));
  opacity: 1;
}

#message-preview :deep(a) {
  color: rgb(var(--v-theme-primary));
  text-decoration: underline;
}
</style>

<style>
#message-preview {
  overflow-wrap: anywhere;
  word-break: break-word;
}

#message-preview pre {
  overflow-wrap: anywhere;
  word-break: break-word;
}

#message-preview ul {
  list-style-type: disc;
  margin-top: 20px;
  margin-left: 15px;
  padding-left: 10px;
  color: rgb(var(--v-theme-grey-darken-4));
}

#message-preview ol {
  list-style-type: decimal;
  margin-top: 20px;
  margin-left: 15px;
  padding-left: 10px;
  color: rgb(var(--v-theme-grey-darken-4));
}
</style>
